/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */

import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import useSWR from 'swr';

import { mergeClasses } from '@utils';

const fetcher = (...args) => fetch(...args).then(res => res.json());

export default function TinyBanner() {
  const languageCode = useMemo(() => window?.config?.language_code || 'en', []);

  const [isOpen, setIsOpen] = useState(false);
  const [isBannerHidden, setIsBannerHidden] = useState(false);

  const { data: tinyBannerData } = useSWR(
    `/api/content/banners/?fields=*&locale=${languageCode}`,
    fetcher
  );

  const tinyBanner = useMemo(
    () => tinyBannerData?.items?.[0],
    [tinyBannerData]
  );

  useEffect(() => {
    const isSessionStorageAvailable = () => {
      try {
        const testKey = '__test__';
        window.sessionStorage.setItem(testKey, testKey);
        window.sessionStorage.removeItem(testKey);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (isSessionStorageAvailable()) {
      const hideTinyBanner = window.sessionStorage.getItem('hideTinyBanner');

      if (hideTinyBanner) {
        const hiddenTime = parseInt(hideTinyBanner, 10);

        const currentTime = new Date().getTime();

        const hoursPassed = (currentTime - hiddenTime) / (1000 * 60 * 60);

        if (hoursPassed >= 24) {
          window.sessionStorage.removeItem('hideTinyBanner');
          setIsBannerHidden(false);
        } else {
          setIsBannerHidden(true);
        }
      } else {
        setIsBannerHidden(false);
      }
    }
  }, []);

  const hideBanner = () => {
    setIsBannerHidden(true);
    try {
      const currentTime = new Date().getTime();
      window.sessionStorage.setItem('hideTinyBanner', currentTime.toString());
    } catch (e) {
      // Todo: Handle Error.
    }
  };

  if (!tinyBanner || isBannerHidden) return null;

  return (
    <>
      <div className="relative">
        <div
          className={mergeClasses(
            'relative z-10 flex h-12 w-full items-center justify-between gap-2 overflow-hidden px-4 pr-12 text-center text-sm text-black hover:cursor-pointer lg:justify-center'
          )}
          style={{
            backgroundImage: tinyBanner.background_gradient
          }}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
        >
          <div className="inline-block max-h-full text-left font-semibold leading-tight">
            <p
              className="line-clamp-2 lg:line-clamp-1"
              style={{
                color: tinyBanner?.title_color
              }}
            >
              {tinyBanner.title}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            hideBanner();
          }}
          className="absolute right-2 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border-none bg-black/10 hover:bg-black/20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 transition-transform"
            style={{
              color: tinyBanner?.title_color
            }}
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <Transition show={isOpen} as={Fragment}>
        <Dialog className="relative z-10" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <Dialog.Panel className="fixed inset-x-0 top-[112px] max-h-screen">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-200"
              enterFrom="transform-gpu -translate-y-full"
              enterTo="transform-gpu translate-y-0"
              leave="ease-in-out duration-200"
              leaveFrom="transform-gpu translate-y-0"
              leaveTo="transform-gpu -translate-y-full"
            >
              <div
                className="relative h-fit w-full space-y-8 px-4 py-8 lg:flex lg:items-start lg:gap-32 lg:space-y-0 lg:px-12 lg:py-12 xl:gap-64"
                style={{
                  backgroundImage: tinyBanner.background_gradient
                }}
              >
                <span
                  className="text-3xl font-extrabold uppercase leading-none tracking-tight lg:w-96 lg:text-4xl"
                  style={{
                    color: tinyBanner.title_color
                  }}
                >
                  {tinyBanner.subtitle}
                </span>
                <div className="grid grow grid-cols-1 gap-4 lg:grid-cols-2">
                  {tinyBanner?.links?.[0]?.value?.map(({ label, url }) => (
                    <a
                      key={url}
                      href={url}
                      className="text-2xl font-bold underline outline-none focus:outline-none"
                      style={{
                        color: tinyBanner.title_color
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            </Transition.Child>
          </Dialog.Panel>
        </Dialog>
      </Transition>
    </>
  );
}
